import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, AreaChart, Area,
} from 'recharts';
import { calcularWidget } from '../../api/dashboards';

const CORES_SERIE = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#20c997', '#fd7e14', '#6c757d'];

function Centro({ children }) {
  return <div className="widget-centro">{children}</div>;
}

function WidgetCard({ widget, onEditar, onRemover, modoEdicao }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    const r = await calcularWidget(widget.tipo, widget.config || {});
    if (r && r.erro) setErro(r.erro);
    setDados(r);
    setCarregando(false);
  }, [widget.tipo, widget.config]);

  useEffect(() => { carregar(); }, [carregar]);

  const renderConteudo = () => {
    if (carregando) return <Centro><i className="fas fa-spinner fa-spin" /> Calculando…</Centro>;
    if (erro) return <Centro><span className="text-danger">{erro}</span></Centro>;
    if (!dados) return <Centro><span className="text-muted">Sem dados.</span></Centro>;

    switch (widget.tipo) {
      case 'sla_cumprimento': {
        const series = (dados.series || []).filter((s) => s.valor > 0);
        if (!series.length) return <Centro><span className="text-muted">Nenhum chamado fechado no período.</span></Centro>;
        return (
          <div className="widget-sla">
            <div className="widget-sla-num">
              <span className="widget-big" style={{ color: dados.percentual >= 90 ? '#198754' : dados.percentual >= 70 ? '#ffc107' : '#dc3545' }}>
                {dados.percentual}%
              </span>
              <span className="widget-sub">no prazo · {dados.total_fechados} fechados</span>
            </div>
            <div className="widget-chart">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={series} dataKey="valor" nameKey="nome" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {series.map((s, i) => <Cell key={i} fill={s.cor || CORES_SERIE[i % CORES_SERIE.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      }
      case 'fila_analista': {
        const series = dados.series || [];
        if (!series.length) return <Centro><span className="text-muted">Nenhum chamado em aberto.</span></Centro>;
        return (
          <ResponsiveContainer width="100%" height={Math.max(160, series.length * 34)}>
            <BarChart data={series} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="nome" width={130} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="valor" name="Abertos" fill="#0d6efd" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'lead_cycle': {
        return (
          <div className="widget-duo">
            <div className="widget-duo-item">
              <span className="widget-big">{dados.lead_texto}</span>
              <span className="widget-sub">Lead time (calendário)</span>
            </div>
            <div className="widget-duo-item">
              <span className="widget-big">{dados.cycle_texto}</span>
              <span className="widget-sub">Cycle time (horas úteis)</span>
            </div>
            <div className="widget-amostra">{dados.amostra} chamado(s) na amostra</div>
          </div>
        );
      }
      case 'fluxo_status': {
        const series = dados.series || [];
        const chaves = dados.status || [];
        if (!series.length) return <Centro><span className="text-muted">Sem movimentações no período.</span></Centro>;
        return (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series} margin={{ left: 4, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {chaves.map((k, i) => (
                <Area key={k} type="monotone" dataKey={k} stackId="1"
                  stroke={CORES_SERIE[i % CORES_SERIE.length]} fill={CORES_SERIE[i % CORES_SERIE.length]} fillOpacity={0.5} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
      }
      case 'velocity': {
        const series = dados.series || [];
        if (!series.length) return <Centro><span className="text-muted">Nenhuma sprint encontrada.</span></Centro>;
        return (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series} margin={{ left: 4, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="concluidos" name="Concluídos" fill="#198754" radius={[4, 4, 0, 0]} />
              <Bar dataKey="total" name="Total" fill="#cfe2ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'contador': {
        return (
          <Centro>
            <div className="widget-contador">
              <span className="widget-big">{dados.valor}</span>
              <span className="widget-sub">chamados</span>
            </div>
          </Centro>
        );
      }
      case 'repositorios': {
        const series = dados.series || [];
        if (!series.length) return <Centro><span className="text-muted">Nenhum repositório.</span></Centro>;
        return (
          <div>
            <div className="widget-duo">
              <div className="widget-duo-item"><span className="widget-big">{dados.total_arquivos}</span><span className="widget-sub">arquivos</span></div>
              <div className="widget-duo-item"><span className="widget-big">{dados.total_commits}</span><span className="widget-sub">commits</span></div>
              <div className="widget-duo-item"><span className="widget-big">{dados.total_repos}</span><span className="widget-sub">repositórios</span></div>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(140, series.length * 30)}>
              <BarChart data={series} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="arquivos" name="Arquivos" fill="#0d6efd" radius={[0, 4, 4, 0]} />
                <Bar dataKey="commits" name="Commits" fill="#6f42c1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }
      case 'hardware': {
        const series = (dados.series || []).filter((s) => s.valor > 0);
        if (!series.length) return <Centro><span className="text-muted">Nenhum hardware cadastrado.</span></Centro>;
        return (
          <div className="widget-sla">
            <div className="widget-sla-num">
              <span className="widget-big">{dados.total}</span>
              <span className="widget-sub">ativos</span>
            </div>
            <div className="widget-chart">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={series} dataKey="valor" nameKey="nome" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {series.map((s, i) => <Cell key={i} fill={s.cor || CORES_SERIE[i % CORES_SERIE.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      }
      case 'sprints': {
        const series = (dados.series || []).filter((s) => s.valor > 0);
        if (!series.length) return <Centro><span className="text-muted">Nenhuma sprint.</span></Centro>;
        return (
          <div className="widget-sla">
            <div className="widget-sla-num">
              <span className="widget-big">{dados.total}</span>
              <span className="widget-sub">sprints</span>
            </div>
            <div className="widget-chart">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={series} dataKey="valor" nameKey="nome" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {series.map((s, i) => <Cell key={i} fill={s.cor || CORES_SERIE[i % CORES_SERIE.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      }
      default:
        return <Centro><span className="text-muted">Tipo desconhecido.</span></Centro>;
    }
  };

  return (
    <div className={`widget-card ${widget.largura === 'inteira' ? 'widget-inteira' : 'widget-meia'}`}>
      <div className="widget-card-head">
        <span className="widget-card-titulo">{widget.titulo || rotuloTipo(widget.tipo)}</span>
        <div className="widget-card-acoes">
          <button type="button" className="btn btn-link btn-sm p-0" title="Recarregar" onClick={carregar}>
            <i className="fas fa-sync-alt" />
          </button>
          {modoEdicao && (
            <>
              <button type="button" className="btn btn-link btn-sm p-0 ms-2" title="Editar" onClick={() => onEditar && onEditar(widget)}>
                <i className="fas fa-cog" />
              </button>
              <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-danger" title="Remover" onClick={() => onRemover && onRemover(widget)}>
                <i className="fas fa-trash" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="widget-card-body">{renderConteudo()}</div>
    </div>
  );
}

function rotuloTipo(tipo) {
  return {
    sla_cumprimento: 'Cumprimento de SLA', fila_analista: 'Fila por analista',
    lead_cycle: 'Lead / Cycle time', fluxo_status: 'Fluxo de status',
    velocity: 'Velocity', contador: 'Contador',
    repositorios: 'Repositórios', hardware: 'Hardware', sprints: 'Sprints',
  }[tipo] || tipo;
}

export default WidgetCard;
